import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { getMyPermissions } from "../services/api";

/** Lisanslı modül kodları — backend Services/Licensing/Modules ile birebir. */
export type ModuleCode = "invoicing" | "stock" | "dynamic-reports" | "kanban" | "receivables" | "purchasing" | "shipping" | "appointments" | "hr" | "finance";

export const MODULE_NAMES: Record<ModuleCode, string> = {
  invoicing: "Fatura (e-Belge)",
  stock: "Stok Yönetimi",
  "dynamic-reports": "Dinamik Raporlar",
  kanban: "Atölye Kanban",
  receivables: "Cari Hesap ve Tahsilat",
  purchasing: "Satın Alma",
  shipping: "Sevkiyat",
  appointments: "Randevu ve Takvim",
  hr: "İnsan Kaynakları",
  finance: "Finans (Kasa, Banka, Mizan)",
};

export interface LicenseInfo {
  isValid: boolean;
  status: string;
  customer: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
}

interface PermissionState {
  permissions: string[];
  role: string | null;
  roleId: number | null;
  loaded: boolean;
  /** Rol için izinler tanımlıysa true. Değilse (henüz kurulmadıysa) sistem permissive davranır. */
  configured: boolean;
  /** Belirli granular izin kodu var mı? Örn. has("cost:categories:create") */
  has: (code: string) => boolean;
  /** Ekrana erişim (o ekran altında herhangi bir izin var mı?) */
  hasScreen: (screenCode: string) => boolean;
  /** Lisansta açık modüller. Sunucu lisansta olmayan modülün izinlerini zaten süzer;
   *  bu liste ekran içi koşullu alanlar (stok bağlantısı, üretim stoğu vb.) içindir. */
  modules: string[];
  hasModule: (code: ModuleCode) => boolean;
  license: LicenseInfo | null;
  refresh: () => Promise<void>;
}

const PermissionContext = createContext<PermissionState | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [role, setRole] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<number | null>(null);
  const [modules, setModules] = useState<string[]>([]);
  const [license, setLicense] = useState<LicenseInfo | null>(null);
  const [loaded, setLoaded] = useState(false);

  const reset = () => {
    setPermissions([]);
    setRole(null);
    setRoleId(null);
    setModules([]);
    setLicense(null);
  };

  const load = useCallback(async () => {
    try {
      const res = await getMyPermissions();
      setPermissions(res.data?.permissions || []);
      setRole(res.data?.role ?? null);
      setRoleId(res.data?.roleId ?? null);
      setModules(res.data?.modules || []);
      setLicense(res.data?.license ?? null);
    } catch {
      reset();
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setLoaded(false);
      load();
    } else {
      reset();
      setLoaded(false);
    }
  }, [isAuthenticated, load]);

  // Tek doğruluk kaynağı backend: /Role/my-permissions API'nin zorladığı etkin listeyi
  // döner (lisansta olmayan modüllerin izinleri çıkarılmış). Arayüz bu listeyi aynen
  // uygular — buton görünürlüğü ile API'nin kabul ettiği işlem hep aynı olur.
  const configured = permissions.length > 0;

  const has = useCallback(
    (code: string) => permissions.includes(code),
    [permissions],
  );

  const hasScreen = useCallback(
    (screenCode: string) => permissions.some((p) => p.startsWith(`${screenCode}:`)),
    [permissions],
  );

  const hasModule = useCallback(
    (code: ModuleCode) => modules.includes(code),
    [modules],
  );

  return (
    <PermissionContext.Provider
      value={{
        permissions, role, roleId, loaded, configured,
        has, hasScreen, modules, hasModule, license, refresh: load,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionState => {
  const ctx = useContext(PermissionContext);
  if (!ctx)
    throw new Error("usePermissions must be used within a PermissionProvider");
  return ctx;
};
